import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchFoldersByProject, createFolder, Folder } from '../../../store/folderSlice';

// ─── Icons ────────────────────────────────────────────────────────────────────

const FolderIcon = ({ open }: { open: boolean }) => (
  <svg
    className="w-4 h-4 flex-shrink-0"
    fill={open ? '#3b82f6' : 'none'}
    stroke={open ? '#3b82f6' : 'currentColor'}
    viewBox="0 0 24 24"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
    />
  </svg>
);

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    className={`w-3 h-3 flex-shrink-0 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

// ─── Tree builder ─────────────────────────────────────────────────────────────

interface TreeNode extends Folder {
  children: TreeNode[];
}

function buildTree(folders: Folder[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  folders.forEach(f => map.set(f._id, { ...f, children: [] }));

  const roots: TreeNode[] = [];
  map.forEach(node => {
    if (node.parentId && node.parentType === 'Folder' && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach(n => sort(n.children));
  };
  sort(roots);
  return roots;
}

// ─── Inline name input ────────────────────────────────────────────────────────

interface InlineInputProps {
  onConfirm: (name: string) => void;
  onCancel: () => void;
  placeholder?: string;
}

const InlineInput: React.FC<InlineInputProps> = ({ onConfirm, onCancel, placeholder = 'Folder name' }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.trim()) onConfirm(value.trim());
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="flex items-center gap-1 px-2 py-1">
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (!value.trim()) onCancel(); }}
        placeholder={placeholder}
        className="flex-1 text-sm border border-blue-400 rounded px-2 py-0.5 outline-none focus:ring-1 focus:ring-blue-500"
      />
      <button
        onClick={() => { if (value.trim()) onConfirm(value.trim()); }}
        disabled={!value.trim()}
        className="text-xs text-blue-600 font-medium disabled:opacity-40"
      >
        Add
      </button>
      <button onClick={onCancel} className="text-xs text-gray-400">
        Cancel
      </button>
    </div>
  );
};

// ─── Tree node ────────────────────────────────────────────────────────────────

interface FolderNodeProps {
  node: TreeNode;
  selectedFolderId?: string | null;
  onSelectFolder: (id: string | null) => void;
  onCreateSubfolder: (parentId: string, name: string) => void;
  depth: number;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  node,
  selectedFolderId,
  onSelectFolder,
  onCreateSubfolder,
  depth,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const isSelected = selectedFolderId === node._id;
  const hasChildren = node.children.length > 0 || addingChild;

  return (
    <div>
      <div
        className={`group flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer text-sm select-none ${
          isSelected
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => {
          onSelectFolder(node._id);
          if (node.children.length > 0) setExpanded(e => !e);
        }}
      >
        {/* Chevron — only shown when there are children */}
        <span
          className={`w-4 flex items-center justify-center ${hasChildren ? 'opacity-100' : 'opacity-0'}`}
          onClick={e => {
            e.stopPropagation();
            setExpanded(e2 => !e2);
          }}
        >
          <ChevronIcon expanded={expanded} />
        </span>

        <FolderIcon open={isSelected} />
        <span className="truncate">{node.name}</span>

        {/* Add subfolder button */}
        <button
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-opacity shrink-0"
          title="New subfolder"
          onClick={e => {
            e.stopPropagation();
            setExpanded(true);
            setAddingChild(true);
          }}
        >
          <PlusIcon />
        </button>
      </div>

      {/* Children */}
      {expanded && (
        <div>
          {node.children.map(child => (
            <FolderNode
              key={child._id}
              node={child}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              onCreateSubfolder={onCreateSubfolder}
              depth={depth + 1}
            />
          ))}
          {addingChild && (
            <div style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}>
              <InlineInput
                onConfirm={name => {
                  onCreateSubfolder(node._id, name);
                  setAddingChild(false);
                }}
                onCancel={() => setAddingChild(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FolderTree ───────────────────────────────────────────────────────────────

interface FolderTreeProps {
  projectId: string;
  onSelectFolder: (folderId: string | null) => void;
  selectedFolderId?: string | null;
}

const FolderTree: React.FC<FolderTreeProps> = ({ projectId, onSelectFolder, selectedFolderId }) => {
  const dispatch = useAppDispatch();
  const { folders, loading, error } = useAppSelector(state => state.folders);
  const [addingRoot, setAddingRoot] = useState(false);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchFoldersByProject(projectId));
    }
  }, [projectId, dispatch]);

  const handleCreateSubfolder = (parentId: string, name: string) => {
    dispatch(createFolder({ projectId, name, parentId, parentType: 'Folder' }));
  };

  const handleCreateRoot = (name: string) => {
    dispatch(createFolder({ projectId, name, parentType: 'Project' }));
    setAddingRoot(false);
  };

  const tree = buildTree(folders);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Folders</span>
        <button
          onClick={() => setAddingRoot(true)}
          className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
          title="New folder"
        >
          <PlusIcon />
        </button>
      </div>

      {/* Tree body */}
      <div className="flex-1 overflow-y-auto py-1">
        {loading && folders.length === 0 && (
          <div className="px-4 py-3 text-sm text-gray-400">Loading...</div>
        )}

        {error && (
          <div className="px-3 py-2 text-xs text-red-500">{error}</div>
        )}

        {/* Root "All" entry */}
        <div
          className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer text-sm select-none mx-1 ${
            selectedFolderId == null
              ? 'bg-blue-50 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
          onClick={() => onSelectFolder(null)}
        >
          <span className="w-4" />
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" />
          </svg>
          <span>All folders</span>
        </div>

        {/* Folder tree nodes */}
        {tree.map(node => (
          <FolderNode
            key={node._id}
            node={node}
            selectedFolderId={selectedFolderId}
            onSelectFolder={onSelectFolder}
            onCreateSubfolder={handleCreateSubfolder}
            depth={0}
          />
        ))}

        {/* Root inline input */}
        {addingRoot && (
          <InlineInput
            onConfirm={handleCreateRoot}
            onCancel={() => setAddingRoot(false)}
          />
        )}

        {!loading && !error && folders.length === 0 && !addingRoot && (
          <div className="px-4 py-4 text-center text-sm text-gray-400">
            No folders yet.
            <button
              onClick={() => setAddingRoot(true)}
              className="block mx-auto mt-1 text-blue-600 hover:underline text-xs"
            >
              Create the first folder
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderTree;
